import React from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import {
  APP_TOP_BAR_ACTION_HIT_PX,
} from 'layout/appTopBarLayout';
import { useVoxPontImmersiveChrome } from './voxPontImmersiveChrome';

/**
 * In-flow restore control shown while FusionMSI chrome is hidden.
 * Lives in AppPageShell so every interpreter fork gets it (landing + live).
 */
export function VoxPontImmersiveRestoreBar() {
  const { pathname } = useLocation();
  const { showRestoreControl, setImmersive } = useVoxPontImmersiveChrome(pathname);

  if (!showRestoreControl) return null;

  return (
    <Box
      component="header"
      data-testid="voxpont-immersive-restore-bar"
      sx={{
        flexShrink: 0,
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        px: 1,
        pt: 'max(4px, env(safe-area-inset-top, 0px))',
        pb: 0.25,
      }}
    >
      <Button
        size="small"
        color="inherit"
        startIcon={<FullscreenExitIcon />}
        onClick={() => setImmersive(false)}
        data-testid="voxpont-show-navigation"
        sx={{
          minHeight: APP_TOP_BAR_ACTION_HIT_PX,
          color: 'text.secondary',
          textTransform: 'none',
        }}
      >
        Show navigation
      </Button>
    </Box>
  );
}

/**
 * Global top-bar control to re-enter immersive mode after the user restored chrome.
 */
export function VoxPontImmersiveEnterButton() {
  const { pathname } = useLocation();
  const { showEnterControl, setImmersive } = useVoxPontImmersiveChrome(pathname);

  if (!showEnterControl) return null;

  return (
    <Button
      size="small"
      color="inherit"
      startIcon={<FullscreenIcon />}
      onClick={() => setImmersive(true, { requestBrowserFullscreen: true })}
      data-testid="voxpont-enter-fullscreen"
      sx={{
        mr: 'auto',
        minHeight: APP_TOP_BAR_ACTION_HIT_PX,
        color: 'text.secondary',
        textTransform: 'none',
        flexShrink: 0,
      }}
    >
      Full screen
    </Button>
  );
}

/**
 * Settings-dialog toggle so the preference is also reachable from the VoxPont gear.
 */
export function VoxPontImmersiveSettingsToggle() {
  const { pathname } = useLocation();
  const { showSettingsToggle, hideGlobalChrome, setImmersive } =
    useVoxPontImmersiveChrome(pathname);

  if (!showSettingsToggle) return null;

  return (
    <Box sx={{ mt: 2 }} data-testid="voxpont-immersive-settings">
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: 'block', mb: 0.5 }}
      >
        Phone layout
      </Typography>
      <FormControlLabel
        sx={{ alignItems: 'flex-start', ml: 0, mr: 0, width: '100%' }}
        control={
          <Switch
            checked={hideGlobalChrome}
            onChange={(_, checked) =>
              setImmersive(checked, { requestBrowserFullscreen: checked })
            }
            inputProps={{ 'aria-label': 'Full screen on this phone' }}
            data-testid="voxpont-fullscreen-switch"
          />
        }
        label={
          <Box sx={{ pl: 1 }}>
            <Typography variant="body2">Full screen on this phone</Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Hides Settings, Notifications, Help, Account, and Apps so VoxPont
              fills the screen. Use Show navigation at the top to bring them back.
            </Typography>
          </Box>
        }
      />
    </Box>
  );
}
